import React, { useMemo, useState } from "react";
import Button from "../../utils/Button";
import Input from "../../utils/Input";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import { toast } from "react-hot-toast";
import { validateEmail, validateFullname, validateOrganisation } from "../../utils/InputVal";
import { useApiMutation } from "../../hooks/useMutation";
import { ApiRequestOptions } from "../../utils/api";
import { ORG_TYPES } from "../../utils/Constant";

interface Props {
    onClose: () => void;
    role: string;
}



export default function AdminInviteModal({ onClose, role }: Props) {
    const [fullName, setFullName] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [orgType, setOrgType] = useState("NONPROFIT_501C3");
    const [showInfo, setShowInfo] = useState(false);
    const [primaryContact, setPrimaryContact] = useState("");
    const [publicWebsite, setPublicWebsite] = useState("");
    const [enabledModules, setEnabledModules] = useState({
        voterRegistrationCheck: false,
        stateRegistrationRedirect: false,
        pollingLocationLookup: false,
        electionReminderTools: false,
        canvasserFieldTools: false,
    });


    const normalizedRole = useMemo(
        () => (role ? role.replace(/\s+/g, "_").toUpperCase() : ""),
        [role]
    );
    const [selectedRole, setSelectedRole] = useState<string>(
        normalizedRole === "SYSTEM_ADMIN" ? "PARTNER" : "CANVASSER"
    );

    const isSystemAdmin = normalizedRole === "SYSTEM_ADMIN";
    const isPartner = normalizedRole === "PARTNER" || normalizedRole === "PARTNER_ADMIN";
    const isSupervisor = normalizedRole === "SUPERVISOR";
    const canInviteTeamMember = isPartner || isSupervisor;
    const isCanvasser = normalizedRole === "CANVASSER";
    const selectedInviteRoleLabel = selectedRole === "SUPERVISOR" ? "supervisor" : "canvasser";

    const inviteMutation = useApiMutation({
        onMutate: () => {
            toast.loading(
                isSystemAdmin
                    ? 'Registering partner organization...'
                    : `Sending ${selectedInviteRoleLabel} invitation...`,
                { id: 'invite-toast' }
            );
        },
        onSuccess: () => {
            toast.success(
                isSystemAdmin
                    ? 'Partner organization registered successfully!'
                    : `${selectedInviteRoleLabel.charAt(0).toUpperCase() + selectedInviteRoleLabel.slice(1)} invitation sent successfully!`,
                { id: 'invite-toast' }
            );
            onClose();
        },
        onError: (error: any) => {
            toast.dismiss('invite-toast');

            const errorData = error?.response?.data;
            
            if (errorData?.data && typeof errorData.data === 'object') {
                Object.entries(errorData.data).forEach(([field, message]) => {
                    toast.error(`${message}`, { id: `error-${field}` });
                });
            } else {
                toast.error(
                    errorData?.message || (
                        isSystemAdmin
                            ? 'Partner registration failed. Please try again.'
                            : `${selectedInviteRoleLabel.charAt(0).toUpperCase() + selectedInviteRoleLabel.slice(1)} invitation failed. Please try again.`
                    ),
                    { id: 'invite-toast' }
                );
            }
        },

    });


    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const nameErr = validateFullname(fullName);
        const emailErr = validateEmail(email);
        if (nameErr || emailErr) {
            toast.error('Please fix validation errors before submitting');
            return;
        }

        const inviteRole = isSystemAdmin
            ? "PARTNER"
            : isSupervisor
                ? "CANVASSER"
                : isPartner
                ? selectedRole
                : "";

        if (!inviteRole) {
            toast.error(isCanvasser ? "Canvassers cannot send invites." : 'Select who you want to invite.');
            return;
        }

        const body = { fullName, email, role: inviteRole };
        let requestOptions: ApiRequestOptions | null = null;

        try {
             if (isSystemAdmin) {
                const selectedModules = [];
                if (enabledModules.voterRegistrationCheck) selectedModules.push("VOTER_REGISTRATION_CHECK");
                if (enabledModules.stateRegistrationRedirect) selectedModules.push("STATE_REGISTRATION_REDIRECT");
                if (enabledModules.pollingLocationLookup) selectedModules.push("POLLING_LOCATION_LOOKUP");
                if (enabledModules.electionReminderTools) selectedModules.push("ELECTION_REMINDER_TOOLS");
                if (enabledModules.canvasserFieldTools) selectedModules.push("CANVASSER_FIELD_TOOLS");
                
                requestOptions = {
                    method: 'POST',
                    url: '/admin/partners/register',
                    data: {
                        email,
                        name: displayName,
                        orgType: orgType,
                        website: publicWebsite,
                        enabledModules: selectedModules,
                        contact: primaryContact,
                        legalName: fullName
                    },
                };
            }
            else if (inviteRole === 'CANVASSER' || inviteRole === 'SUPERVISOR') {
                requestOptions = {
                    method: 'POST',
                    url: '/partner/invite/user',
                    data: body,
                };
            }

            if (!requestOptions) {
                toast.error('Unsupported role selected.');
                return;
            }

            await inviteMutation.mutateAsync(requestOptions);
        } catch (err: any) {
            console.error("Invite failed", err?.response?.data?.message);
            // Error is handled by the mutation's onError callback
        }
    };

    return (
        <div
            className="fixed inset-0 z-[99] flex items-center justify-center"
            aria-modal="true"
            role="dialog"
        >
            <div
                className="absolute inset-0 bg-black/50"
                onMouseDown={onClose}
            />


            <form
                onSubmit={handleSubmit}
                className="relative z-10 w-[720px] h-[600px] overflow-y-auto max-w-[95%] bg-gradient-to-br from-gray-900 via-slate-900 to-black rounded-xl shadow-2xl p-6 text-white border border-gray-800"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="my-3 rounded-md bg-gray-800/70 backdrop-blur shadow-lg p-2 shadow-black w-fit">
                        <AutoAwesomeMosaicIcon fontSize="large" color="warning" />
                    </div>

                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">✕</button></div>
                <div className="flex items-center relative justify-between">
                    <div className="flex gap-2 relative items-center">
                        <h2 className="text-2xl font-semibold capitalize">
                            {isSystemAdmin
                                ? "Register New Organization"
                                : canInviteTeamMember
                                    ? isSupervisor ? "Invite a canvasser" : "Invite a team member"
                                    : "Invites unavailable"}
                        </h2>

                        {isSystemAdmin && (
                            <button className="text-gray-300 hover:text-white" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)} type="button">
                                <InfoOutlineIcon fontSize="small" />
                            </button>
                        )}
                    </div>
                    {showInfo && isSystemAdmin && (
                        <div className="absolute bottom-6 left-0 w-96 bg-gray-800 text-white p-3 rounded shadow-lg z-20">
                            <p className="text-sm">
                                Register partner organizations with access to platform modules and tools.
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-xs mb-5 text-gray-300">
                    {isSystemAdmin
                        ? "Complete this form to register a new partner organization."
                        : canInviteTeamMember
                            ? isSupervisor
                                ? "Supervisors can invite canvassers to support field operations."
                                : "Partners and partner admins can invite canvassers or supervisors to support field operations."
                            : "Your role cannot send invitations."}
                </p>

                {/* System Admin - Partner Registration Form */}
                {isSystemAdmin && (
                    <>
                        <div className="space-y-4">
                            <Input
                                label="Organization's Legal name"
                                name="legalName"
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                onValidate={(val) => validateOrganisation(val, "Legal Name")}
                                validationDebounce={200}
                                placeholder="JohnDoe LLC"
                                required
                                size="large"
                            />
                            <Input
                                label="Public Display Name"
                                name="displayName"
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                onValidate={(val) => validateOrganisation(val, "Display Name")}
                                validationDebounce={200}
                                placeholder="John Doe"
                                required
                                size="large"
                            />
                            <Input
                                label="Organisation Email"
                                type="email"
                                name="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onValidate={validateEmail}
                                validationDebounce={200}
                                placeholder="jane@example.com"
                                required
                                size="large"
                            />
                            <Input
                                label="Primary Contact"
                                type="text"
                                name="phoneNumber"
                                value={primaryContact}
                                onChange={(e) => setPrimaryContact(e.target.value)}
                                validationDebounce={200}
                                onValidate={(value) => {
                                    if (!value) return 'Primary contact is required';
                                    // Remove all non-digit characters to validate
                                    const digitsOnly = value.replace(/\D/g, '');
                                    if (digitsOnly.length !== 10) {
                                        return 'Phone number must be exactly 10 digits (US format)';
                                    }
                                    return null;
                                }}
                                placeholder="+1 (555) 123-4567"
                                required
                                size="large"
                            />
                            <Input
                                label="Public Website"
                                type="text"
                                name="website"
                                value={publicWebsite}
                                onChange={(e) => setPublicWebsite(e.target.value)}
                                validationDebounce={200}
                                onValidate={(value) => {
                                    if (!value) return null;
                                    if (!value.startsWith('https://')) {
                                        return 'Website URL must start with https://';
                                    }
                                    try {
                                        new URL(value);
                                        return null;
                                    } catch {
                                        return 'Please enter a valid URL';
                                    }
                                }}
                                placeholder="https://example.com"
                                size="large"
                            />
                            <small className="text-gray-400 text-xs">This information is used for compliance transparency and audit records.</small>
                            <div>
                                <label className="block text-sm font-medium text-gray-200 mb-1">Select Organization Type</label>
                                <select
                                    value={orgType}
                                    onChange={(e) => setOrgType(e.target.value)}
                                    className="w-full bg-gray-800 text-white border border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                >
                                    {ORG_TYPES.map((type) => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-lg font-medium text-gray-200 my-3">Enable Platform Modules</label>
                                <div className="space-y-2">
                                    <label className="flex items-center text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={enabledModules.voterRegistrationCheck}
                                            onChange={(e) => setEnabledModules(prev => ({ ...prev, voterRegistrationCheck: e.target.checked }))}
                                            className="mr-2 h-4 w-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-400 focus:ring-2"
                                        />
                                        Voter Registration Check
                                    </label>
                                    <label className="flex items-center text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={enabledModules.stateRegistrationRedirect}
                                            onChange={(e) => setEnabledModules(prev => ({ ...prev, stateRegistrationRedirect: e.target.checked }))}
                                            className="mr-2 h-4 w-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-400 focus:ring-2"
                                        />
                                        State Registration Redirect
                                    </label>
                                    <label className="flex items-center text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={enabledModules.pollingLocationLookup}
                                            onChange={(e) => setEnabledModules(prev => ({ ...prev, pollingLocationLookup: e.target.checked }))}
                                            className="mr-2 h-4 w-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-400 focus:ring-2"
                                        />
                                        Polling Location Lookup
                                    </label>
                                    <label className="flex items-center text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={enabledModules.electionReminderTools}
                                            onChange={(e) => setEnabledModules(prev => ({ ...prev, electionReminderTools: e.target.checked }))}
                                            className="mr-2 h-4 w-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-400 focus:ring-2"
                                        />
                                        Election Reminder Tools
                                    </label>
                                    <label className="flex items-center text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={enabledModules.canvasserFieldTools}
                                            onChange={(e) => setEnabledModules(prev => ({ ...prev, canvasserFieldTools: e.target.checked }))}
                                            className="mr-2 h-4 w-4 text-amber-500 bg-gray-800 border-gray-600 rounded focus:ring-amber-400 focus:ring-2"
                                        />
                                        Canvasser Field Tools
                                    </label>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Partner - Team Invitation Form */}
                {canInviteTeamMember && (
                    <div className="space-y-4">
                        <Input
                            label="Full Name"
                            name="fullName"
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            onValidate={validateFullname}
                            validationDebounce={200}
                            placeholder="John Doe"
                            required
                            size="large"
                        />
                        <Input
                            label="Email Address"
                            type="email"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onValidate={validateEmail}
                            validationDebounce={200}
                            placeholder="jane@example.com"
                            required
                            size="large"
                        />
                        <div>
                            <label className="block text-sm font-medium text-gray-200 mb-1">Role</label>
                            {isSupervisor ? (
                                <div className="w-full bg-gray-800 text-white border border-gray-700 rounded px-3 py-2">
                                    Canvasser
                                </div>
                            ) : (
                                <select
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                    className="w-full bg-gray-800 text-white border border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                >
                                    <option value="CANVASSER">Canvasser</option>
                                    <option value="SUPERVISOR">Supervisor</option>
                                </select>
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-6 flex justify-center w-full gap-3">

                    <div className="flex-1">
                        <Button
                            disabled={
                                !email ||
                                !fullName ||
                                inviteMutation.isPending ||
                                isCanvasser ||
                                (isSystemAdmin && (!displayName || !primaryContact))
                            }
                            type="submit"
                            variant="primary"
                            label={inviteMutation.isPending ? "Sending..." : isSystemAdmin ? "Register Partner" : "Send Invite"}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
}
